"use client";

import { signOut as betterAuthSignOut, useSession } from "@/lib/auth/client";
import type { AuthUser } from "@/lib/auth/server";
import { toError } from "@/lib/logging/error";
import { logger } from "@/lib/logging/logger";
import { usePathname, useRouter } from "next/navigation";
import { createContext, memo, useCallback, useContext } from "react";

interface AuthContextType {
    user: AuthUser | null;
    loading: boolean;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true, signOut: async () => {} });

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

export const AuthProvider = memo(function AuthProvider({ children }: { children: React.ReactNode; }) {
    const router = useRouter();
    const pathname = usePathname();
    const { data: session, isPending } = useSession();

    const fallbackPath = typeof window !== "undefined" ? window.location.pathname : "";
    const isCrmRoute = (pathname ?? fallbackPath).startsWith("/crm");

    // For non-CRM routes, we don't need auth state
    const loading = isCrmRoute ? isPending : false;
    const user: AuthUser | null = isCrmRoute ? session?.user ? { id: session.user.id, email: session.user.email ?? "", name: session.user.name ?? null, image: session.user.image ?? null, emailVerified: Boolean(session.user.emailVerified), createdAt: session.user.createdAt, updatedAt: session.user.updatedAt } : null : null;

    const signOut = useCallback(async () => {
        try {
            await betterAuthSignOut();
            router.push("/");
            router.refresh();
        } catch (error) {
            logger("ui").error("Error signing out:", toError(error));
        }
    }, [router]);

    return <AuthContext.Provider value={{ user, loading, signOut }} data-component="AuthProvider" data-location="src/components/ui/providers/auth-provider.tsx">{children}</AuthContext.Provider>;
});
